import { useEffect, useMemo, useState } from "react";
import {
  BadgePercent,
  Coins,
  Copy,
  Gift,
  IndianRupee,
  Mail,
  Send,
  Share2,
  Users
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import api from "../services/api";
import { getToken } from "../services/auth";
import { jwtDecode } from "jwt-decode";

export default function ReferralAffiliate() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const user = useMemo(() => {
    try {
      const token = getToken();
      return token ? jwtDecode(token) : { role: "student" };
    } catch {
      return { role: "student" };
    }
  }, []);

  const loadRewards = () => {
    setLoading(true);
    api.get("/referrals/me")
      .then(res => setData(res.data))
      .catch(() => toast.error("Failed to load referral rewards"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadRewards();
  }, []);

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setSending(true);
    try {
      await api.post("/referrals/invite", { referred_email: email });
      setEmail("");
      toast.success("Referral invite added");
      loadRewards();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add invite");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-950">
      <Toaster position="top-center" />
      
      <main className="flex-1 p-4 sm:p-6 lg:p-10 space-y-8 min-w-0">
        <section className="relative overflow-hidden rounded-lg bg-slate-950 px-5 py-8 text-white shadow-2xl shadow-slate-900/10 sm:px-8 lg:px-10">
          <div className="absolute right-0 top-0 h-56 w-56 translate-x-20 -translate-y-20 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-black uppercase tracking-widest text-emerald-100">
                <Share2 size={14} /> Referral & Affiliate
              </div>
              <h1 className="text-3xl font-black tracking-tight sm:text-4xl">Earn Rewards by Inviting Friends</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300 sm:text-base">
                Students earn coins, cashback, and discounts when they invite friends to join learning.
              </p>
            </div>

            <div className="rounded-lg border border-white/10 bg-white/10 p-4 backdrop-blur">
              <p className="text-xs font-black uppercase tracking-widest text-slate-400">Your Code</p>
              <div className="mt-2 flex items-center gap-3">
                <span className="text-2xl font-black tracking-widest">{data?.code || "..."}</span>
                <button
                  onClick={() => data?.code && copyText(data.code, "Referral code")}
                  className="rounded-lg bg-white/10 p-2 hover:bg-white/20"
                >
                  <Copy size={16} />
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-5 md:grid-cols-3">
          {[
            { label: "Coins Earned", value: data?.totals?.coins || 0, icon: Coins, color: "bg-amber-500" },
            { label: "Cashback", value: `₹${data?.totals?.cashback || 0}`, icon: IndianRupee, color: "bg-emerald-500" },
            { label: "Discounts", value: data?.totals?.discounts || 0, icon: BadgePercent, color: "bg-indigo-500" },
          ].map(item => (
            <div key={item.label} className="surface p-5 flex items-center gap-4">
              <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${item.color} text-white shadow-lg`}>
                <item.icon size={22} />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-slate-400">{item.label}</p>
                <p className="text-2xl font-black text-slate-950 dark:text-white">{item.value}</p>
              </div>
            </div>
          ))}
        </section>

        <section className="grid gap-6 xl:grid-cols-3">
          <div className="surface p-5 sm:p-6 xl:col-span-2">
            <div className="mb-5 flex items-center gap-3">
              <Gift className="text-emerald-600" size={24} />
              <div>
                <h2 className="text-xl font-black text-slate-950 dark:text-white">Invite a Friend</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">Add an email or copy your referral link.</p>
              </div>
            </div>

            <form onSubmit={sendInvite} className="grid gap-3 sm:grid-cols-[1fr_auto]">
              <div className="relative">
                <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="email"
                  className="form-control pl-11"
                  placeholder="friend@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
              </div>
              <button disabled={sending} className="btn-primary">
                {sending ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" /> : <Send size={16} />}
                Send Invite
              </button>
            </form>

            <div className="mt-5 flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/60 sm:flex-row sm:items-center sm:justify-between">
              <p className="min-w-0 truncate text-sm font-bold text-slate-600 dark:text-slate-300">{data?.share_url || "Loading referral link..."}</p>
              <button
                onClick={() => data?.share_url && copyText(data.share_url, "Referral link")}
                className="btn-secondary shrink-0"
              >
                <Copy size={16} /> Copy Link
              </button>
            </div>
          </div>

          <div className="surface p-5 sm:p-6">
            <h2 className="text-xl font-black text-slate-950 dark:text-white">Reward Rules</h2>
            <div className="mt-5 space-y-3">
              {(data?.rules || []).map(rule => (
                <div key={rule.label} className="rounded-lg bg-slate-50 p-4 dark:bg-slate-950/60">
                  <p className="font-black text-slate-900 dark:text-white">{rule.label}</p>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    {rule.coins} coins, ₹{rule.cashback} cashback, {rule.discount_percent}% discount
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="surface p-5 sm:p-6">
          <div className="mb-5 flex items-center gap-3">
            <Users className="text-indigo-600" size={24} />
            <div>
              <h2 className="text-xl font-black text-slate-950 dark:text-white">Referral History</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Track invites and earned rewards.</p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="text-sm">
              <thead>
                <tr className="text-left text-xs font-black uppercase tracking-widest text-slate-400">
                  <th className="py-3 pr-4">Friend</th>
                  <th className="py-3 pr-4">Status</th>
                  <th className="py-3 pr-4">Coins</th>
                  <th className="py-3 pr-4">Cashback</th>
                  <th className="py-3 pr-4">Discount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {loading ? (
                  <tr><td colSpan={5} className="py-8 text-center text-slate-400">Loading rewards...</td></tr>
                ) : data?.referrals?.length ? (
                  data.referrals.map(item => (
                    <tr key={item.id}>
                      <td className="py-4 pr-4 font-bold text-slate-900 dark:text-white">{item.referred_email}</td>
                      <td className="py-4 pr-4 capitalize text-slate-600 dark:text-slate-300">{item.status}</td>
                      <td className="py-4 pr-4 text-amber-600 font-black">{item.coins}</td>
                      <td className="py-4 pr-4 text-emerald-600 font-black">₹{item.cashback}</td>
                      <td className="py-4 pr-4 text-indigo-600 font-black">{item.discount_percent}%</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={5} className="py-8 text-center text-slate-400">No referrals yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
